/**
 * Utility functions for determining fair status and date handling
 */

/**
 * Helper function to format a Date object to YYYY-MM-DD in local timezone
 */
function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export type FairStatus = 'upcoming' | 'ongoing' | 'past';

export interface FairStatusInfo {
  status: FairStatus;
  daysUntil?: number;
  daysRemaining?: number;
}

/**
 * Get the current fair status based on start and end dates
 * Uses local timezone for date comparisons to match user's perspective
 */
export function getFairStatus(startDate?: string, endDate?: string): FairStatusInfo {
  if (!startDate || !endDate) {
    return { status: 'upcoming' }; // Default for fairs without dates
  }

  // Parse dates - append 'T00:00:00' to ensure local timezone interpretation
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T23:59:59');

  // Validate dates - return default if invalid
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.warn('Invalid date format provided to getFairStatus:', { startDate, endDate });
    return { status: 'upcoming' }; // Fallback for invalid dates
  }

  const now = new Date();

  if (now > end) {
    return { status: 'past' };
  }

  if (now >= start && now <= end) {
    const daysRemaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return { status: 'ongoing', daysRemaining };
  }

  // Upcoming
  const daysUntil = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return { status: 'upcoming', daysUntil };
}

/**
 * Get today's date in YYYY-MM-DD format
 */
export function getTodayString(): string {
  return toLocalDateString(new Date());
}

/**
 * Get date string for N days from now
 */
export function getDateString(daysFromNow: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return toLocalDateString(date);
}

/**
 * Get start and end dates for "This Week"
 */
export function getThisWeekDates(): { start: string; end: string } {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday
  const start = new Date(today);
  start.setDate(today.getDate() - dayOfWeek); // Start of week (Sunday)

  const end = new Date(start);
  end.setDate(start.getDate() + 6); // End of week (Saturday)

  return {
    start: toLocalDateString(start),
    end: toLocalDateString(end)
  };
}

/**
 * Get start and end dates for "This Month"
 */
export function getThisMonthDates(): { start: string; end: string } {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return {
    start: toLocalDateString(start),
    end: toLocalDateString(end)
  };
}

/**
 * Format date range for display (Issue #1672 - proper timezone handling)
 * Parses dates as UTC to avoid timezone shifts that can change the displayed date
 */
export function formatDateRange(startDate?: string, endDate?: string): string {
  if (!startDate || !endDate) return '';

  // Parse as UTC to prevent timezone shifts
  const start = new Date(startDate + 'T00:00:00Z');
  const end = new Date(endDate + 'T00:00:00Z');

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';

  // Format using UTC timezone to display the exact calendar date
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  };

  if (startDate === endDate) {
    return start.toLocaleDateString('en-US', options);
  }

  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
}

/**
 * Parse a date-only string (YYYY-MM-DD) as a local-timezone Date. Issue #1672:
 * `new Date("2026-05-24")` is parsed as UTC midnight and shifts to the previous
 * day in negative-offset timezones; this preserves the calendar date the user
 * entered. Returns null for empty/invalid input. ISO timestamps with a `T`
 * component (e.g. `2026-05-24T12:00:00Z`) already carry timezone info, so they
 * are returned as a plain `new Date(...)`.
 */
export function parseDateOnly(dateString?: string | null): Date | null {
  if (!dateString) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const date = dateOnly ? new Date(dateString + 'T00:00:00') : new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a date-only string (YYYY-MM-DD) for display (Issue #1672 - proper timezone handling).
 * Parses as UTC and formats with UTC timezone to avoid date shifts.
 * Returns empty string for missing/invalid input.
 */
export function formatDateOnly(
  dateString?: string | null,
  options?: Intl.DateTimeFormatOptions,
  locale: string = 'en-US'
): string {
  if (!dateString) return '';

  // Parse as UTC to prevent timezone shifts
  const date = new Date(dateString + 'T00:00:00Z');
  if (isNaN(date.getTime())) return '';

  // Ensure UTC timezone is used to display the exact calendar date
  const utcOptions = { ...options, timeZone: 'UTC' };
  return date.toLocaleDateString(locale, utcOptions);
}
